/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { RestApiService } from '../services/rest-api.service';
import { StorageService } from '../services/storage.service';
import { Observable, Subject } from 'rxjs';
import { AppSettings } from '../app.settings';
import { environment } from '../../../../environments/environment';
import * as CryptoJS from 'crypto-js';

@Injectable()
export class UserCommonService {
  public isMetadataLoaded: boolean = false;
  timeZones;
  changeBrand = new Subject();
  metaApiLoaded = new Subject<void>();
  private brandOptionsCache: any[] | null = null;
  private mapScriptLoadPromise: Promise<void> | null = null;
  public weekCalenderData: {
    weekEvents,
    weekViewResources,
    checkedCondition: boolean,
    addShiftResource,
    startEndDate: '',
  };

  constructor(private restApiService: RestApiService,
    private storageService: StorageService) {
    this.getAdminMetaData();
  }

  openCloseSidebar() {
    if (screen.width < 992) {
      if (!document.getElementsByTagName('body')[0].classList.contains('closed')) {
        document.getElementsByTagName('body')[0].classList.toggle('closed');
      }
    }
  }

  setGoogleMapScript() {
    return this.loadGoogleMapScriptIfNeeded();
  }

  /** True when Maps Places library is usable (SearchLocationDirective needs Autocomplete). */
  private isPlacesLibraryReady(): boolean {
    return !!(window as any).google?.maps?.places?.Autocomplete;
  }

  /** Poll until Places attaches (loading=async can defer libraries briefly after script onload). */
  private waitForPlacesLibrary(maxMs = 10000, stepMs = 50): Promise<boolean> {
    return new Promise(resolve => {
      const start = Date.now();
      const tick = () => {
        if (this.isPlacesLibraryReady()) {
          resolve(true);
          return;
        }
        if (Date.now() - start >= maxMs) {
          resolve(false);
          return;
        }
        setTimeout(tick, stepMs);
      };
      tick();
    });
  }

  private resolveWhenPlacesReady(
    resolve: () => void,
    reject: (e: Error) => void
  ): void {
    void this.waitForPlacesLibrary().then(ok => {
      if (!ok) {
        this.mapScriptLoadPromise = null;
        reject(new Error('Google Maps Places library not available'));
        return;
      }
      this.changeMapScriptLoadFlag();
      resolve();
    });
  }

  loadGoogleMapScriptIfNeeded(): Promise<void> {
    if (this.isPlacesLibraryReady()) {
      this.changeMapScriptLoadFlag();
      return Promise.resolve();
    }

    if (this.mapScriptLoadPromise) {
      return this.mapScriptLoadPromise;
    }

    this.mapScriptLoadPromise = new Promise((resolve, reject) => {
      const key = environment.mapKey;
      const existingScript = document.querySelector('script[data-google-maps-script="true"]') as HTMLScriptElement | null;
      if (existingScript) {
        let bootstrapStarted = false;
        const afterMapsBootstrapped = () => {
          if (bootstrapStarted) {
            return;
          }
          bootstrapStarted = true;
          this.resolveWhenPlacesReady(resolve, reject);
        };

        if ((window as any).google?.maps) {
          afterMapsBootstrapped();
        } else {
          existingScript.addEventListener('load', afterMapsBootstrapped, { once: true });
          existingScript.addEventListener('error', () => {
            this.mapScriptLoadPromise = null;
            reject(new Error('Google Maps script failed to load'));
          }, { once: true });
          // If `load` already fired before this listener was attached, detect on next microtask.
          queueMicrotask(() => {
            if ((window as any).google?.maps) {
              afterMapsBootstrapped();
            }
          });
        }
        return;
      }

      const googleAPIScript = document.createElement('script');
      googleAPIScript.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry&loading=async`;
      googleAPIScript.async = true;
      googleAPIScript.defer = true;
      googleAPIScript.setAttribute('data-google-maps-script', 'true');
      googleAPIScript.onload = () => {
        this.resolveWhenPlacesReady(resolve, reject);
      };
      googleAPIScript.onerror = () => {
        this.mapScriptLoadPromise = null;
        reject(new Error('Google Maps script failed to load'));
      };
      document.head.appendChild(googleAPIScript);
    });

    return this.mapScriptLoadPromise;
  }

  changeMapScriptLoadFlag() {
    this.isMetadataLoaded = true;
    this.metaApiLoaded.next();
  }


  getAdminMetaData() {
    this.getMetaData().subscribe(result => {
      this.timeZones = result.timezoneList;
    });
  }

  getEncryptPass(pass) { //** Note: dont remove commented code */
    if(pass){
      const encryptedBase64Key = AppSettings.LOGIN_SECRET;
      const parsedBase64Key = CryptoJS.enc.Base64.parse(encryptedBase64Key);
      let encryptedData = null;
      {
        // Encryption process
        const plaintText = pass;
        // this is Base64-encoded encrypted data
        encryptedData = CryptoJS.AES.encrypt(plaintText, parsedBase64Key, {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }).toString();
      }
      return encryptedData;
    }
    return '';  
  }

  getDecryptPass(encryptedData: string): string {
    if(encryptedData){
    const encryptedBase64Key = AppSettings.LOGIN_SECRET;
    const parsedBase64Key = CryptoJS.enc.Base64.parse(encryptedBase64Key);
    
    const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, parsedBase64Key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });

      const decryptedText = decryptedBytes.toString(CryptoJS.enc.Utf8);
      return decryptedText;
    }
    return '';
  }

  getUpdatedStoredValue(key) {
    const loginData = JSON.parse(this.storageService.getItem(AppSettings.USER_DETAILS_LOGIN_DATA));
    if (key == AppSettings.START_DAY_OF_WEEK_FOR_MONTH) {
      if (loginData) {
        if (loginData[AppSettings.START_DAY_OF_WEEK_FOR_MONTH]) {
          return loginData[AppSettings.START_DAY_OF_WEEK_FOR_MONTH];
        } else {
          return 0;
        }
      }
    }
  }

   getUpdatedStoredSettingsValueFromStorage(key) {
    const settingsData = JSON.parse(this.storageService.getItem(AppSettings.USER_SETTINGS_DATA));
    if (key == AppSettings.GRID_LIMIT_RECORDS) {
      if (settingsData) {
        if (settingsData[AppSettings.GRID_LIMIT_RECORDS]) {
          return +settingsData[AppSettings.GRID_LIMIT_RECORDS];
        } else {
          return 15;
        }
      }
    }
  }

  getInvoiceStatusToDisplay(key) {
    let status = '';
    switch (key) {
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.DRAFT:
        status = 'Draft';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.SENT:
        status = 'Sent';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.PAID:
        status = 'Paid';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.PARTIAL_PAID:
        status = 'Partially Paid';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.OVERDUE:
        status = 'Overdue';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.QUOTATION_DRAFT:
        status = 'Quote Draft';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.QUOTATION_SENT:
        status = 'Quote Sent';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.QUOTATION_ACCEPTED:
        status = 'Quote Accepted';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.QUOTATION_REJECTED:
        status = 'Quote Rejected';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.REFUND:
        status = 'Refund';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.PARTIAL_REFUND:
        status = 'Partial Refund';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.DEPOSIT_PAID:
        status = 'Deposit Paid';
        break;
      case AppSettings.INVOICE_STATUS_FILTER_VALUES.DEPOSIT_OVERDUE:
        status = 'Deposit Overdue';
        break;
      default:
      break;
    }
    return status;
  }

  getInvoiceTypeFromStatus(status){
    let invoiceType = AppSettings.INVOICE_TYPE_VALUES.INVOICE;
    if(status.includes(AppSettings.INVOICE_TYPE_VALUES.QUOTATION.toLocaleLowerCase())){
      invoiceType = AppSettings.INVOICE_TYPE_VALUES.QUOTATION; 
    }
    return invoiceType;
  }


  getMetaData(): Observable<any> {
    return this.restApiService.get('Get MetaData', `/metadata`, 'page-center');
  }

  signOut(): Observable<any> {
    return this.restApiService.delete('delete', '/secure/sessions', 'page-center');
  }

  signIn(d, data): Observable<any> {
    return this.restApiService.post(d, '/sessions', data, 'page-center');
  }

  sessionRole(data): Observable<any> {
    return this.restApiService.post('password', '/secure/session/role', data, 'page-center');
  }
  
  checkVersion(): Observable<any> {
    return this.restApiService.get('Check Version', '/version', 'page-center');
  }

  setPassword(data): Observable<any> {
    return this.restApiService.post('password', '/users/set-password', data, 'page-center');
  }

  getUserProfile(): Observable<any> {
    return this.restApiService.get('profile', '/secure/users/profile', 'page-center');
  }

  forgotPassword(data): Observable<any> {
    return this.restApiService.post('password', '/users/forgot-password', data, 'page-center');
  }

  fetchStripe(code, state): Observable<any> {
    return this.restApiService.get('stripe', `/stripe-connect/authorization-redirect-uri?code=${code}&state=${state}`, 'page-center');
  }

  // marketing start
  activeInactiveSegment(data, segmentId): Observable<any> {
    return this.restApiService.put('activeInactiveSegment', `/secure/segments/status/${segmentId}`, data, 'page-center');
  }

  getUserDefinedRoles(): Observable<any> {
    return this.restApiService.get('getUserDefinedRoles', `/secure/roles-list`, 'page-center');
  }

  getUsers(roleId): Observable<any> {
    return this.restApiService.get('get-users', `/secure//users-list/${roleId}`, 'page-center');
  }

  addSegment(data): Observable<any> {
    return this.restApiService.post('addSegment', '/secure/segments', data, 'page-center');
  }

  getSegmentsList(): Observable<any> {
    return this.restApiService.get('getSegmentsList', `/secure/segments/segments-list`, 'page-center');
  }

  addNotification(data): Observable<any> {
    return this.restApiService.post('addmotification', '/secure/admin-announcement', data, 'page-center');
  }

  resendNotification(id): Observable<any> {
    return this.restApiService.post('resendNotification', `/secure/admin-announcement/${id}/resend`, '', 'page-center');
  }
  // marketing end

  getWaffleMenu(): Observable<any> {
    return this.restApiService.get('getWaffleMenu', `/secure/waffle-menu?applyActivatedStatus=true&activated=true&searchText=&offset=0&limit=999&sortColumn=indexOrder&sortType=asc`, 'page-center');
  }

  getBrandListForFilter(id): Observable<any> {
    return this.restApiService.get('getBrandList', `/secure/franchise-brands/${id}/list?deleted=false`, 'page-center');
  }

  /**
   * Store brand options in service cache
   */
  setBrandOptions(brandOptions: any[]): void {
    this.brandOptionsCache = brandOptions;
    this.storageService.setItem('brandOptionsCache', brandOptions);
  }

  /**
   * Get brand options from cache
   */
  getBrandOptions(): any[] | null {
    if (Array.isArray(this.brandOptionsCache) && this.brandOptionsCache.length) {
      return this.brandOptionsCache;
    }
    const cached = this.storageService.getItem('brandOptionsCache');
    if (!cached) {
      return null;
    }
    try {
      let parsed: any = cached;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      this.brandOptionsCache = Array.isArray(parsed) ? parsed : null;
      return this.brandOptionsCache;
    } catch (e) {
      console.error('Error parsing cached brand options:', e);
      return null;
    }
  }

  /**
   * Clear brand options cache
   */
  clearBrandOptionsCache(): void {
    this.brandOptionsCache = null;
    this.storageService.removeItem('brandOptionsCache');
  }

  getBrandListForStaff(id): Observable<any> {
    return this.restApiService.get('getBrandList', `/secure/staffs-brand/${id}/list`, 'page-center');
  }

  getNotification(): Observable<any> {
    return this.restApiService.get('getNotification', `/secure/notification-request/grid/data?offset=0&limit=999`, 'page-center');
  }

  getNotificationCount(): Observable<any> {
    return this.restApiService.get('getNotification', `/secure/notification-request-count`, 'page-center');
  }

  updateNotification(notificationRequestId): Observable<any> {
    return this.restApiService.put('updateNotification', `/secure/notification-request-viewed/${notificationRequestId}`, 'page-center');
  }

  getLoginAs(d, data): Observable<any> {
    return this.restApiService.post(d, '/sessions', data, 'page-center');
  }

  getAccessPermissions(): Observable<any> {
    return this.restApiService.get('Access Permissions', '/secure/accessgroup', 'page-center');
  }

  getStaffAccessPermissions(userId): Observable<any> {
    return this.restApiService.get('Access Permissions', `/secure/access-permissions/${userId}`, 'page-center');
  }

  updateAccessPermissions(data, userId): Observable<any> {
    return this.restApiService.put('activeInactiveSegment', `/secure/access-permissions/${userId}`, data, 'page-center');
  }

  uploadCroppedImage(url, data): Observable<any> {
    return this.restApiService.post('uploadCroppedImage', `${url}`, data, 'page-center');
  }

  deleteImage(clientId, fileId): Observable<any> {
    return this.restApiService.delete('deleteImage', `/secure/${clientId}/client-banner-files/${fileId}`,
      'page-center');
  }

  removeSpaceBetweenNumber(number) {
    if (number) {
      return number.replace(/\D/g, '');
    } else {
      return '';
    }
  }

  readAllNotifications(): Observable<any> {
    return this.restApiService.put('activeInactiveSegment', `/secure/notification-request/read-all`, '', 'page-center');
  }

  getAdminGraphicsSettings(brandId): Observable<any> {
    return this.restApiService.get('getAdminGraphicsSettings', `/secure/master-banners/${brandId}`, 'page-center');
  }

  getUserSettings(userId): Observable<any> {
    if(userId === 'admin') {
      return this.restApiService.get('getUserSettings', `/secure/super-settings`, 'page-center');
    } else {
      return this.restApiService.get('getUserSettings', `/secure/settings/${userId}`, 'page-center');
    }
  }

}